import cn from 'classnames'
import {
  Button,
  CategoryIcon,
  isCategorySlug,
  SkillIcon,
  isSkillSlug,
  IconStarDefault,
} from '@mc/design-system'
import { TrackingContainer } from '@mc/visibility'
import { useTrendingTitle } from 'experiments/pen-5038_v1_mega_content_picker/hooks/useTrendingTitle'
import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'

import { trackCategoryPickerViewed } from './events'
import styles from './FilterPicker.module.scss'

export type FilterPickerProps = {
  selections: Array<Selection>
  selectedSlug: string
  setSelectedSlug(slug: string): void
  onSelect?(slug: string, id?: string): void
}

export type Selection = { name: string; slug: string; id?: string }

export const FilterPicker = ({
  selections,
  selectedSlug,
  setSelectedSlug,
  onSelect,
}: FilterPickerProps) => {
  const trendingTitle = useTrendingTitle(TRENDING_SLUG)

  return (
    <TrackingContainer onFirstVisible={trackCategoryPickerViewed}>
      <div
        className={cn(
          'mc-my-5 mc-py-1 mc-mx-xl-5',
          'd-flex flex-nowrap flex-sm-wrap',
          'justify-content-sm-center',
          styles.categoryPicker,
        )}
      >
        {selections.map((selection) => (
          <FilterButton
            key={selection.slug}
            selection={selection}
            isSelected={selection.slug === selectedSlug}
            onSelect={(slug: string, id: string) => {
              if (onSelect) {
                const categoryName =
                  slug === TRENDING_SLUG ? trendingTitle : slug
                onSelect(categoryName, id)
              }
              setSelectedSlug(slug)
            }}
          />
        ))}
      </div>
    </TrackingContainer>
  )
}

type FilterButtonProps = {
  selection: Selection
  isSelected: boolean
  onSelect(slug: string, id?: string): void
}

const FilterButton = ({
  selection,
  isSelected,
  onSelect,
}: FilterButtonProps) => {
  const getIcon = () => {
    if (selection.slug === 'trending') {
      return <IconStarDefault />
    }

    if (isCategorySlug(selection.slug)) {
      return <CategoryIcon slug={selection.slug} />
    }

    if (isSkillSlug(selection.slug)) {
      return <SkillIcon slug={selection.slug} />
    }

    return <></>
  }

  return (
    <Button
      onClick={() => onSelect(selection.slug, selection.id)}
      kind='secondary'
      size='md'
      className={cn(
        'd-sm-flex mc-p-2 mc-py-sm-3 mc-px-sm-4',
        styles.filterButton,
        isSelected && styles.active,
      )}
    >
      <span className={cn('mc-mr-3 d-none d-lg-block', styles.icon)}>
        {getIcon()}
      </span>
      {selection.name}
    </Button>
  )
}
